import type { ReactNode } from "react";

import { formatCount } from "../lib/format";
import { ui } from "../lib/ui";
import { Info } from "./Info";

/** A change against the previous period of the same length: a fraction
 *  (0.12 = up 12%) or, for a rate, a difference in percentage points. */
export interface Delta {
  value: number;
  unit: "percent" | "points";
}

interface StatCardProps {
  label: string;
  /** A count (formatted here) or an already-formatted string. */
  value: number | string | null;
  /** What the value is in — "people", "EGP", "days" — shown after it. Every
   *  card has one, so a number is never read without knowing its unit. */
  unit: string;
  /** What the figure means and how it is computed — the ⓘ tooltip. */
  about: string;
  hint?: ReactNode;
  /** Colour of the tile's accent stripe — one of the chart series, so the
   *  tile and the series it summarises share a hue. */
  color?: string;
  /** Movement vs the previous period; null = no previous data to compare. */
  delta?: Delta | null;
  /** What the delta is measured against, e.g. "vs previous 90 days". */
  deltaLabel?: string;
  /** Up is bad for this metric (returns, say): flips the delta's colour. */
  invert?: boolean;
}

function DeltaBadge({ delta, invert }: { delta: Delta; invert: boolean }) {
  const up = delta.value > 0;
  const flat = Math.abs(delta.value) < (delta.unit === "points" ? 0.0005 : 0.005);
  const good = flat ? null : up !== invert;
  const tone = flat
    ? "text-muted bg-[color-mix(in_srgb,var(--muted)_12%,transparent)]"
    : good
      ? "text-success bg-[color-mix(in_srgb,var(--success)_12%,transparent)]"
      : "text-danger bg-[color-mix(in_srgb,var(--danger)_12%,transparent)]";
  const text =
    delta.unit === "points"
      ? `${Math.abs(delta.value * 100).toFixed(1)} pt`
      : `${Math.abs(Math.round(delta.value * 100))}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums ${tone}`}>
      <span aria-hidden="true">{flat ? "–" : up ? "▲" : "▼"}</span>
      <span className="sr-only">{flat ? "unchanged" : up ? "up" : "down"}</span>
      {text}
    </span>
  );
}

/** One headline number, with its movement against the previous period. */
export function StatCard({
  label,
  value,
  unit,
  about,
  hint,
  color = "var(--accent)",
  delta,
  deltaLabel,
  invert = false,
}: StatCardProps) {
  const text = typeof value === "number" || value === null ? formatCount(value) : value;
  return (
    <div
      className={`${ui.card} relative flex flex-col gap-1 overflow-hidden px-[18px] py-4 text-xs`}
      style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${color} 9%, var(--surface)), var(--surface) 55%)` }}
    >
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1" style={{ background: color }} />
      <span className={`${ui.label} flex items-center gap-1.5`}>
        {label}
        <Info text={about} />
      </span>
      <span className="flex flex-wrap items-baseline gap-2">
        <span className="text-[28px] font-semibold leading-tight">{text}</span>
        <span className="text-[13px] font-medium text-muted">{unit}</span>
        {delta && <DeltaBadge delta={delta} invert={invert} />}
      </span>
      {(hint || (delta && deltaLabel)) && (
        <span className="text-muted">
          {hint}
          {hint && delta && deltaLabel ? " · " : ""}
          {delta && deltaLabel}
        </span>
      )}
    </div>
  );
}
